import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useDashboardAlerts, DashboardAlert } from "@/hooks/useDashboardAlerts";
import { AlertTriangle, CreditCard, Package, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const ALERT_ICONS = {
  expiring_subscription: AlertTriangle,
  pending_payment: CreditCard,
  low_stock: Package,
};

const ALERT_COLORS = {
  expiring_subscription: "text-status-warning-foreground bg-status-warning",
  pending_payment: "text-status-danger-foreground bg-status-danger",
  low_stock: "text-status-warning-foreground bg-status-warning",
};

interface AlertCardProps {
  alert: DashboardAlert;
}

function AlertCard({ alert }: AlertCardProps) {
  const navigate = useNavigate();
  const Icon = ALERT_ICONS[alert.type];

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={cn("p-1.5 rounded-lg", ALERT_COLORS[alert.type])}>
              <Icon className="h-4 w-4" />
            </div>
            <CardTitle className="text-sm font-medium">{alert.title}</CardTitle>
          </div>
          {/* `countLabel` vaut « 10+ » quand la liste serveur est tronquée :
              afficher « 10 » laissait croire à un total exact. */}
          <Badge variant="secondary">{alert.countLabel}</Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="space-y-1.5 mb-3">
          {alert.items.slice(0, 3).map((item) => (
            <li key={item.id} className="text-xs text-muted-foreground flex justify-between">
              <span className="truncate">{item.label}</span>
              {item.detail && <span className="text-xs opacity-70">{item.detail}</span>}
            </li>
          ))}
        </ul>
        <Button 
          variant="ghost" 
          size="sm" 
          className="w-full justify-between text-xs h-8"
          onClick={() => navigate(alert.linkTo)}
        >
          {alert.linkLabel}
          <ChevronRight className="h-3 w-3" />
        </Button>
      </CardContent>
    </Card>
  );
}

export function AlertsWidget() {
  const { alerts, isLoading } = useDashboardAlerts();

  if (isLoading) {
    return <div className="animate-pulse h-32 bg-muted rounded-lg" />;
  }

  if (alerts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {alerts.map((alert) => (
        <AlertCard key={alert.id} alert={alert} />
      ))}
    </div>
  );
}
